/**
 * Naked POC Plugin for LightweightCharts v5
 *
 * Renders naked POC levels: settled-session profile POCs that price has not
 * traded through since that session ended. TWO layers in one plugin:
 *   vp  - volume profile POCs (blob field `levels`), sub-toggle under the
 *         Vol Profile indicator, follows the VP session mode
 *   tpo - TPO (time-price) POCs (blob field `tpo_levels`), sub-toggle under
 *         the TPO Profile indicator, follows the TPO session scope
 * Each level draws as a horizontal ray from its birth (session end) to the
 * chart's right edge with a right-anchored label (nPOC vs nTPO). Levels
 * disappear when the backend's 60s cycle invalidates them (price traded
 * through).
 *
 * Data: one global blob over SSE channel 'naked-poc' (snapshot on connect,
 * update on set changes); session-mode vocabulary per layer: full -> eth,
 * split/eth-rth -> eth + overnight + rth, rth -> eth + rth (eth levels
 * render in every mode; split-session levels are additive).
 */

import { CONFIG } from '../config';
import { authFetch } from '../auth/authFetch';
import { unifiedSSE } from '../hooks/useUnifiedSSE';

const DEBUG = false;
const dlog = (...args) => { if (DEBUG) console.log(...args); };

// Ray + label style per session type (descriptive palette, distinct from the
// VP POC amber so the layers read apart when several are on).
const SESSION_STYLE = {
  eth: { color: 'rgba(232, 121, 249, 0.85)', tag: 'ETH' },
  overnight: { color: 'rgba(103, 232, 249, 0.85)', tag: 'ON' },
  rth: { color: 'rgba(253, 186, 116, 0.85)', tag: 'RTH' },
};

// Per-source label prefix + dash pattern so a coinciding VP and TPO POC on
// the same price still read as two levels.
const SOURCE_STYLE = {
  vp: { prefix: 'nPOC', dash: [6, 3] },
  tpo: { prefix: 'nTPO', dash: [2, 3] },
};

class NakedPOCRenderer {
  constructor(series, chart) {
    this.series = series;
    this.chart = chart;
    this.levels = [];   // [{poc, birth_ts, birth_date, sessionType}]
    this.enabled = false;
  }

  // Bitmap x for a birth timestamp: exact coordinate, else binary-search snap
  // to the nearest bar (renko-style non-uniform time), else 0 when the birth
  // is left of the loaded data (ray enters from the left edge), else null.
  _birthX(ts, hRatio) {
    const exact = this.chart.timeScale().timeToCoordinate(ts);
    if (exact !== null && exact !== undefined) return exact * hRatio;
    const data = (this.series && this.series.data) ? this.series.data() : null;
    if (!data || data.length === 0) return null;
    if (ts <= data[0].time) return 0;
    if (ts >= data[data.length - 1].time) return null;
    let lo = 0, hi = data.length - 1;
    while (lo < hi - 1) { const mid = (lo + hi) >> 1; if (data[mid].time <= ts) lo = mid; else hi = mid; }
    const nearest = (ts - data[lo].time) <= (data[hi].time - ts) ? data[lo].time : data[hi].time;
    const c = this.chart.timeScale().timeToCoordinate(nearest);
    return c !== null && c !== undefined ? c * hRatio : null;
  }

  draw(target) {
    if (!this.enabled || !this.levels.length || !this.series || !this.chart) return;

    target.useBitmapCoordinateSpace(scope => {
      const ctx = scope.context;
      const hR = scope.horizontalPixelRatio;
      const vR = scope.verticalPixelRatio;
      const chartWidth = scope.bitmapSize.width;

      ctx.save();
      ctx.font = `${Math.round(10 * vR)}px sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';

      for (const lvl of this.levels) {
        const style = SESSION_STYLE[lvl.sessionType] || SESSION_STYLE.eth;
        const srcStyle = SOURCE_STYLE[lvl.source] || SOURCE_STYLE.vp;
        const priceY = this.series.priceToCoordinate(lvl.poc);
        if (priceY === null || priceY === undefined) continue;
        const y = priceY * vR;

        const xStart = this._birthX(lvl.birth_ts, hR);
        if (xStart === null) continue;

        ctx.strokeStyle = style.color;
        ctx.lineWidth = 1.5 * vR;
        ctx.setLineDash(srcStyle.dash.map(d => d * hR));
        ctx.beginPath();
        ctx.moveTo(xStart, y);
        ctx.lineTo(chartWidth, y);
        ctx.stroke();
        ctx.setLineDash([]);

        // Right-anchored label with a dark backing box, inline on the line (OI-levels style)
        const label = `${srcStyle.prefix} ${lvl.poc.toFixed(2)} ${lvl.birth_date.slice(5)} ${style.tag}`;
        const textW = ctx.measureText(label).width;
        const pad = 3 * hR;
        const fontPx = Math.round(10 * vR);
        const boxX = chartWidth - textW - 2 * pad;
        ctx.fillStyle = 'rgba(15, 20, 25, 0.85)';
        ctx.fillRect(boxX, y - fontPx / 2 - pad, textW + 2 * pad, fontPx + 2 * pad);
        ctx.fillStyle = style.color;
        ctx.fillText(label, boxX + pad, y);
      }

      ctx.restore();
    });
  }
}

class NakedPOCView {
  constructor(series, chart) {
    this._renderer = new NakedPOCRenderer(series, chart);
  }

  renderer() {
    return this._renderer;
  }
}

class NakedPOCPrimitive {
  constructor(series, chart) {
    this._view = new NakedPOCView(series, chart);
    this._requestUpdate = null;
    this._rafPending = false;
  }

  attached({ requestUpdate }) {
    this._requestUpdate = requestUpdate;
  }

  detached() {
    this._requestUpdate = null;
    this._rafPending = false;
  }

  updateAllViews() {
    return this._view;
  }

  paneViews() {
    return [this._view];
  }

  scheduleUpdate() {
    if (this._requestUpdate && !this._rafPending) {
      this._rafPending = true;
      requestAnimationFrame(() => {
        this._rafPending = false;
        if (this._requestUpdate) this._requestUpdate();
      });
    }
  }

  setLevels(levels, enabled) {
    this._view._renderer.levels = levels;
    this._view._renderer.enabled = enabled;
    this.scheduleUpdate();
  }
}

export class NakedPOCPlugin {
  constructor(chart, series) {
    this.chart = chart;
    this.series = series;
    this.primitive = null;
    // Per-source layers; mode vocabulary: 'full' | 'split' | 'eth-rth' | 'rth'
    this.layers = {
      vp: { enabled: false, mode: 'full' },
      tpo: { enabled: false, mode: 'full' },
    };
    this.data = null; // last blob {as_of_ts, levels: {...}, tpo_levels: {...}}
    this._sseUnsubs = [];
  }

  _anyEnabled() {
    return this.layers.vp.enabled || this.layers.tpo.enabled;
  }

  _sessionTypesFor(mode) {
    // ETH naked levels render in EVERY mode (they are the meaningful object;
    // overnight/rth POCs are almost never naked): split/rth modes ADD the
    // split-session lists rather than replacing the eth one, so a mode
    // switch can never empty the layer.
    if (mode === 'split' || mode === 'eth-rth') return ['eth', 'overnight', 'rth'];
    if (mode === 'rth') return ['eth', 'rth'];
    return ['eth'];
  }

  _selectedLevels() {
    if (!this.data) return [];
    const lists = {
      vp: this.data.levels || {},
      tpo: this.data.tpo_levels || {},
    };
    const out = [];
    for (const source of ['vp', 'tpo']) {
      const layer = this.layers[source];
      if (!layer.enabled) continue;
      for (const st of this._sessionTypesFor(layer.mode)) {
        for (const lvl of (lists[source][st] || [])) {
          out.push({
            poc: lvl.poc,
            birth_ts: lvl.birth_ts,
            birth_date: lvl.birth_date || '',
            sessionType: st,
            source,
          });
        }
      }
    }
    return out;
  }

  _redraw() {
    if (this.primitive) {
      this.primitive.setLevels(this._selectedLevels(), this._anyEnabled());
    }
  }

  _handleBlob(data) {
    if (!data || !data.levels) return;
    this.data = data;
    dlog(`[NAKED POC] blob received: eth=${(data.levels.eth || []).length} ` +
         `overnight=${(data.levels.overnight || []).length} rth=${(data.levels.rth || []).length}`);
    this._redraw();
  }

  _connectSSE() {
    this._disconnectSSE();
    this._sseUnsubs.push(
      unifiedSSE.subscribe('naked-poc', 'naked_poc_snapshot', (d) => this._handleBlob(d))
    );
    this._sseUnsubs.push(
      unifiedSSE.subscribe('naked-poc', 'naked_poc_update', (d) => this._handleBlob(d))
    );
  }

  _disconnectSSE() {
    this._sseUnsubs.forEach(unsub => unsub());
    this._sseUnsubs = [];
  }

  async _fetchInitial() {
    // REST fallback: covers re-enable when the SSE channel stayed connected
    // (no fresh snapshot is emitted in that case).
    try {
      const response = await authFetch(`${CONFIG.apiUrl}/api/indicators/naked-poc?symbol=ES`);
      if (response.ok) {
        this._handleBlob(await response.json());
      }
    } catch (error) {
      console.error('[NAKED POC] initial fetch failed:', error);
    }
  }

  /**
   * Configure both layers in one call:
   *   configure({ vp: {enabled, mode}, tpo: {enabled, mode} })
   * Attaches the primitive + SSE while any layer is enabled, detaches when
   * none. Modes come from the parent indicators' session pickers.
   */
  async configure(layers) {
    for (const source of ['vp', 'tpo']) {
      const next = layers && layers[source];
      if (!next) continue;
      if (typeof next.enabled === 'boolean') this.layers[source].enabled = next.enabled;
      if (next.mode) this.layers[source].mode = next.mode;
    }

    if (this._anyEnabled()) {
      if (!this.primitive) {
        this.primitive = new NakedPOCPrimitive(this.series, this.chart);
        this.series.attachPrimitive(this.primitive);
      }
      this._connectSSE();
      if (!this.data) {
        await this._fetchInitial();
      }
      this._redraw();
    } else {
      this._disconnectSSE();
      if (this.primitive) {
        this.series.detachPrimitive(this.primitive);
        this.primitive = null;
      }
    }
  }

  disable() {
    this.configure({ vp: { enabled: false }, tpo: { enabled: false } });
  }

  isEnabled() {
    return this._anyEnabled();
  }
}

export default NakedPOCPlugin;
